import { ref, computed } from 'vue'
import store from '@/store'
import { config } from '@/config.js'
import { useRoute } from '@/composables/common/router/useRouter.js'
import { useToast } from '@/composables/common/useToast'

export function useRecogidaIndumentarias() {
    const route = useRoute()
    const toast = useToast()

    // ==================== STATE ====================
    const loading = ref(false)
    const marcandoGlobal = ref(null)

    const filters = ref({
        search: '',
        search_for: undefined,
        page: 1,
        per_page: 15
    })

    const meta = ref({})

    const deportistas = ref([])

    const indumentariaFields = [
        { key: 'nombre', label: 'Prenda' },
        { key: 'talla.nombre', label: 'Talla', centered: true },
        { key: 'cantidad', label: 'Cantidad', centered: true },
        { key: 'recogida', label: 'Recogida', centered: true }
    ]

    // ==================== COMPUTED ====================
    const listaId = computed(() => route.params.lista_id)

    function detectSearchFor() {
        const s = filters.value.search
        if (!s) return undefined
        if (/\d/.test(s)) return 'dni'
        return 'nombre'
    }

    // ==================== API CALLS ====================
    function fetchRecogida() {
        loading.value = true
        // src/api.js serializes ONLY `data.filters` into the query string
        // (api.format_url_params). Top-level keys are only used for URL
        // placeholder substitution via format_url_merge_tags, and this route
        // has no placeholders for search/search_for/page/per_page — so those
        // must live INSIDE filters to actually reach the backend.
        // `lista_id` stays at the top level because %lista_id% IS a placeholder.
        const params = {
            filters: {
                search: filters.value.search || undefined,
                search_for: detectSearchFor(),
                page: filters.value.page,
                per_page: filters.value.per_page
            },
            lista_id: listaId.value
        }
        store.dispatch(config.getVuexAlias('back.bases.recogida-indumentarias.listado'), params)
            .then(res => {
                deportistas.value = (res.data.data || []).map(d => ({
                    ...d,
                    indumentarias: (d.indumentarias || []).map(i => ({
                        ...i,
                        _saving: false
                    }))
                }))
                if (res.data.meta) {
                    meta.value = {
                        current_page: res.data.meta.current_page,
                        last_page: res.data.meta.last_page,
                        total: res.data.meta.total,
                        per_page: res.data.meta.per_page
                    }
                }
            })
            .catch(error => {
                console.error('Error fetching recogida:', error)
                toast.error(error?.response?.data?.message || 'Error al cargar deportistas')
            })
            .finally(() => {
                loading.value = false
            })
    }

    function marcarTodas(deportista) {
        marcandoGlobal.value = deportista.id
        const params = {
            filters: {},
            lista_id: listaId.value,
            base_id: deportista.id
        }
        return store.dispatch(config.getVuexAlias('back.bases.recogida-indumentarias.recoger-todas'), params)
            .then(res => {
                deportista.indumentarias.forEach(i => { i.recogida = true })
                return res
            })
            .finally(() => {
                marcandoGlobal.value = null
            })
    }

    function toggleRecogida(deportista, indumentaria, value) {
        const previousValue = indumentaria.recogida
        // Optimistic update: marcar instantáneamente
        indumentaria.recogida = value
        indumentaria._saving = true

        const params = {
            filters: {},
            lista_id: listaId.value,
            base_id: deportista.id,
            indumentaria_id: indumentaria.id,
            recogida: value,
            observaciones: indumentaria.observaciones || null
        }
        return store.dispatch(config.getVuexAlias('back.bases.recogida-indumentarias.recoger-individual'), params)
            .catch(error => {
                // Revertir si la petición falla
                indumentaria.recogida = previousValue
                throw error
            })
            .finally(() => {
                indumentaria._saving = false
            })
    }

    // ==================== HELPERS ====================
    function contarRecogidas(deportista) {
        return deportista.indumentarias.filter(i => i.recogida).length
    }

    function todasRecogidas(deportista) {
        return deportista.indumentarias.every(i => i.recogida)
    }

    return {
        loading,
        filters,
        meta,
        deportistas,
        indumentariaFields,
        marcandoGlobal,
        listaId,
        fetchRecogida,
        marcarTodas,
        toggleRecogida,
        contarRecogidas,
        todasRecogidas
    }
}
